import type { TestimonialsData } from "@/data/types";
import { resolveImagePath } from "@/utils/imageResolver";

interface TestimonialsProps {
  data: TestimonialsData;
}

function StarRating({ rating }: { rating: number }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span
        key={i}
        className={`text-yellow-400 ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
      >
        ★
      </span>
    );
  }
  return <div className="flex">{stars}</div>;
}

export function Testimonials({ data }: TestimonialsProps) {
  const { 
    title, 
    subtitle, 
    overallRating, 
    platformRatings, 
    aiSummary, 
    reviews, 
    writeReviewButton,
    socialButtons 
  } = data;

  return (
    <section className="bg-red-700 text-white relative">
      {/* Curved top edge to match the original */}
      <div className="absolute top-0 left-0 right-0 h-8">
        <svg viewBox="0 0 1200 120" className="w-full h-8">
          <path d="M0,0 C300,120 900,120 1200,0 L1200,120 L0,120 Z" fill="currentColor" />
        </svg>
      </div>
      
      <div className="container mx-auto px-4 py-16 pt-24">
        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-2">{title}</h2>
          <p className="text-xl opacity-90">{subtitle}</p>
        </div>

        {/* Reviews Widget Container */}
        <div className="max-w-4xl mx-auto">
          {/* Platform Ratings Bar */}
          <div className="bg-white text-black rounded-t-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              {platformRatings.map((platform, index) => (
                <div key={index} className="flex items-center gap-2">
                  {platform.icon && (
                    <img 
                      src={resolveImagePath(platform.icon)} 
                      alt={`${platform.platform} logo`}
                      className="w-4 h-4"
                    />
                  )}
                  <span className="font-medium">{platform.platform}</span>
                  {platform.score && (
                    <span className="font-bold">{platform.score}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Overall Rating and Write Review */}
          <div className="bg-gray-100 text-black p-4 flex justify-between items-center">
            <div>
              <div className="text-sm text-gray-600 mb-1">Overall Rating</div>
              <div className="flex items-center gap-2">
                <StarRating rating={Math.floor(overallRating.score)} />
                <span className="text-2xl font-bold">{overallRating.score}</span>
                <span className="text-gray-600">({overallRating.totalReviews})</span>
              </div>
            </div>
            <button className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700 transition-colors">
              {writeReviewButton.text}
            </button>
          </div>

          {/* AI Summary */}
          <div className="bg-white text-black p-6 border-t">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  AI
                </div>
                <span className="font-medium">{aiSummary.title}</span>
                <span className="text-gray-600 text-sm">{aiSummary.subtitle}</span>
              </div>
              <div className="space-y-2">
                {aiSummary.points.map((point, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Individual Reviews */}
            <div className="space-y-4">
              {reviews.slice(0, 3).map((review) => (
                <div key={review.id} className="border-t pt-4 first:border-t-0 first:pt-0">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-sm font-medium">
                      {review.author[0]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{review.author}</span>
                        <span className="text-gray-500 text-sm">{review.timeAgo}</span>
                      </div>
                      <StarRating rating={review.rating} />
                      <p className="text-gray-700 mt-2 leading-relaxed">{review.text}</p>
                      {review.readMoreLink && (
                        <a 
                          href={review.readMoreLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-1 inline-block"
                        >
                          Read more
                        </a>
                      )}
                      {review.source && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">Source: {review.source}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Social Platform Buttons */}
        <div className="flex justify-center mt-8">
          <div className="bg-black bg-opacity-50 rounded-full px-6 py-3 flex items-center gap-4">
            {socialButtons.map((button, index) => (
              <a
                key={index}
                href={button.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src={resolveImagePath(button.icon)}
                  alt={`${button.platform} logo`}
                  className="w-8 h-8 rounded"
                />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Curved bottom edge */}
      <div className="absolute bottom-0 left-0 right-0 h-8 rotate-180">
        <svg viewBox="0 0 1200 120" className="w-full h-8">
          <path d="M0,0 C300,120 900,120 1200,0 L1200,120 L0,120 Z" fill="currentColor" />
        </svg>
      </div>
    </section>
  );
}